import {
    BootstrapConfig,
    RuntimeConfig,
    WorkbenchConfig,
} from "../types/Configs";
import ConfigService from "./ConfigService";

export class ConfigurationManager {
    private static instance: ConfigurationManager;
    private configService: ConfigService;

    private bootstrapConfig: BootstrapConfig | null = null;
    private runtimeConfig: RuntimeConfig | null = null;
    private workbenchConfig: WorkbenchConfig | null = null;

    constructor() {
        this.configService = new ConfigService();
    }

    static getInstance(): ConfigurationManager {
        if (!ConfigurationManager.instance) {
            ConfigurationManager.instance = new ConfigurationManager();
        }
        return ConfigurationManager.instance;
    }

    loadBootstrapConfig(): BootstrapConfig | null {
        if (this.bootstrapConfig) {
            return this.bootstrapConfig;
        }

        // Extract from global RUNTIME_CONFIG
        if (window.RUNTIME_CONFIG) {
            this.bootstrapConfig = {
                backendUrl: window.RUNTIME_CONFIG?.backendUrl as string,
                tasksUrl: window.RUNTIME_CONFIG?.tasksUrl as string,
                pushUrl: window.RUNTIME_CONFIG?.pushUrl as string,
                dataUrl: window.RUNTIME_CONFIG?.dataUrl as string,
                reportsUrl: window.RUNTIME_CONFIG?.reportsUrl as string,
                keycloakConfig: window.RUNTIME_CONFIG?.keycloakConfig as Record<
                    string,
                    unknown
                >,
            };
        }

        return this.bootstrapConfig;
    }

    async loadRuntimeConfig(): Promise<RuntimeConfig> {
        if (this.runtimeConfig) {
            return this.runtimeConfig;
        }

        try {
            const result = await this.configService.loadRuntimeConfig();
            this.runtimeConfig = result.unwrapOr({
                workbenchEnabled: false,
                emailNotifications: false,
            } as RuntimeConfig);
            return this.runtimeConfig;
        } catch (error) {
            console.error("Failed to load runtime config:", error);
            // Return default config
            this.runtimeConfig = {
                workbenchEnabled: false,
                emailNotifications: false,
            };
            return this.runtimeConfig;
        }
    }

    async loadWorkbenchConfig(): Promise<WorkbenchConfig | null> {
        if (this.workbenchConfig) {
            return this.workbenchConfig;
        }

        try {
            const result = await this.configService.loadWorkbenchConfig();
            this.workbenchConfig = result.unwrapOr(null);
            return this.workbenchConfig;
        } catch (error) {
            console.error("Failed to load workbench config:", error);
            return null;
        }
    }

    getBootstrapConfig(): BootstrapConfig | null {
        return this.bootstrapConfig;
    }

    getRuntimeConfig(): RuntimeConfig | null {
        return this.runtimeConfig;
    }

    getWorkbenchConfig(): WorkbenchConfig | null {
        return this.workbenchConfig;
    }

    // Reset cached configs for testing or refresh scenarios
    reset(): void {
        this.bootstrapConfig = null;
        this.runtimeConfig = null;
        this.workbenchConfig = null;
    }
}

export default ConfigurationManager.getInstance();
